/* eslint-disable max-lines */
import React, { Suspense } from 'react';
import * as Sentry from '@sentry/react';
import { Route, Routes, BrowserRouter, Navigate } from 'react-router-dom';

import { ApolloProvider } from '@apollo/react-common';
import { DndProvider } from 'react-dnd';

import { HTML5Backend } from 'react-dnd-html5-backend';
import RouteResetScroll from 'components/Route/ResetScroll/ResetScroll';
import ErrorDialog from 'components/ErrorDialog/ErrorDialog';
import ApplicationProvider from 'components/Application/Provider';
import ApplicationContext from 'components/Application/Context';

import RouteRedirect from 'components/Route/Redirect/Redirect';
import RoutePrivate from 'components/Route/Private/Private';

import ScreensClientForm from 'screens/Client/Form/ScreensClientForm';
import ScreensClientsList from 'screens/Client/List/ScreensClientsList';

import ScreensBrands from 'screens/Brands/ScreensBrands';

import ScreensUser from 'screens/User/ScreensUser';
import ScreensUsers from 'screens/Users/ScreensUsers';
import ScreensCampaign from 'screens/Campaigns/ScreensCampaign';
import ScreensTraditionalCampaigns from 'screens/Traditional/Campaigns/ScreensTraditionalCampaigns';
import ScreensSource from 'screens/Sources/ScreensSource';
import ScreensScan from 'screens/Scan/ScreensScan';
import ScreensProgramForm from 'screens/Program/ScreensProgramForm';

import ScreensProductions from 'screens/Productions/ScreensProductions';
import ScreensTaxonomy from 'screens/Taxonomy/ScreensTaxonomy';
import ScreensProperty from 'screens/Property/ScreensProperty';
import ScreensPerson from 'screens/Person/ScreensPerson';

import ScreensTools from 'screens/Tools/ScreensTools';
import ScreensToolsMediaValue from 'screens/Tools/MediaValue/ScreensToolsMediaValue';

import ScreensPlacements from 'screens/Placements/ScreensPlacements';

import ScreensNotFound404 from 'screens/NotFound404/ScreensNotFound404';

import ScreensDeliverables from 'screens/Deliverables/ScreensDeliverables';
import ScreensSearch from 'screens/Search/ScreensSearch';

import ScreensAuditLogDetails from 'screens/AuditLog/Details/ScreensAuditLogDetails';

import history from 'store/history/history';
import { canlistTools } from 'components/Tools/helpers';

import UILoader from 'components/UI/Loader/Loader';
import { Environment } from 'types';
import ScreensNotPPTeamGate from './NotPPTeamGate/ScreensNotPPTeamGate';
import ScreensToolsWatchReport from './Tools/WatchReport/ScreensToolsWatchReport';
import ScreensToolsSpotlightMismatches from './Tools/SpotlightMismatches/ScreensToolsSpotlightMismatches';
import ScreensToolsViewershipDataAudit from './Tools/ViewershipDataAudit/ScreensToolsViewershipDataAudit';
import ScreensToolsBrandSpotlight from './Tools/BrandSpotlight/ScreensToolsBrandSpotlight';
import ScreensToolsBatchAssignPlacements from './Tools/BatchAssignPlacements/ScreensToolsBatchAssignPlacements';
import ScreensToolsPlacementJsonExport from './Tools/PlacementJsonExport/ScreensToolsPlacementJsonExport';
import { ScreensWatchProductions } from './WatchProductions/ScreensWatchProductions';

const ScreensPayments = React.lazy(
  () => import('screens/Payments/ScreensPayments'),
);

const ScreensDataCleanup = React.lazy(
  () => import('screens/DataCleanup/ScreensDataCleanup'),
);

// eslint-disable-next-line max-lines-per-function
function Root({ environment }: Readonly<{ environment: Environment }>) {
  return (
    <DndProvider backend={HTML5Backend}>
      <ApplicationProvider history={history} environment={environment}>
        <ApplicationContext.Consumer>
          {/* eslint-disable-next-line max-lines-per-function */}
          {({ apolloClient }) => (
            <ApolloProvider client={apolloClient}>
              <BrowserRouter basename="/">
                <ErrorDialog />
                <RouteResetScroll />
                <Routes>
                  <Route
                    path="/campaigns/*"
                    element={<ScreensCampaign />}
                    requiredPermission="campaigns.list"
                  />

                  <Route
                    path="/search"
                    element={<RoutePrivate element={ScreensSearch} />}
                  />
                  <Route
                    path="/clients/list/*"
                    element={
                      <RoutePrivate
                        element={ScreensClientsList}
                        requiredPermission="clients.update"
                      />
                    }
                  />
                  <Route
                    path="/clients/create"
                    element={
                      <RoutePrivate
                        element={ScreensClientForm}
                        requiredPermission="clients.create"
                      />
                    }
                  />
                  <Route
                    path="/clients/new/edit"
                    element={
                      <RoutePrivate
                        element={ScreensClientForm}
                        requiredPermission="clients.create"
                      />
                    }
                  />
                  <Route
                    path="/clients/new"
                    element={
                      <RoutePrivate
                        element={ScreensClientForm}
                        requiredPermission="clients.create"
                      />
                    }
                  />
                  <Route
                    path="/clients/:id/edit"
                    element={
                      <RoutePrivate
                        element={ScreensClientForm}
                        requiredPermission="clients.update"
                      />
                    }
                  />
                  <Route
                    path="/clients/:id"
                    element={<Navigate replace to="edit" />}
                  />
                  <Route path="/brands/*" element={<ScreensBrands />} />
                  <Route path="/taxonomies/*" element={<ScreensTaxonomy />} />
                  <Route path="/properties/*" element={<ScreensProperty />} />
                  <Route path="/people/*" element={<ScreensPerson />} />
                  <Route
                    path="/payments/*"
                    element={
                      <Suspense fallback={<UILoader />}>
                        <ScreensPayments />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/program/:id"
                    element={
                      <RoutePrivate
                        exact
                        element={ScreensProgramForm}
                        requiredPermission="programs.read"
                      />
                    }
                  />
                  <Route
                    path="/tools"
                    element={
                      <RoutePrivate
                        exact
                        element={ScreensTools}
                        grantAccess={canlistTools}
                      />
                    }
                  />
                  <Route
                    path="/tools/mediavalue"
                    element={
                      <RoutePrivate
                        element={ScreensToolsMediaValue}
                        requiredPermission="placements.manage"
                      />
                    }
                  />
                  <Route
                    path="/datacleanup/*"
                    element={
                      <Suspense fallback={<UILoader />}>
                        <ScreensDataCleanup />
                      </Suspense>
                    }
                    requiredPermission="privilegedControls"
                  />
                  <Route
                    path="/tools/watch-report"
                    element={
                      <RoutePrivate
                        element={ScreensToolsWatchReport}
                        requiredPermission="viewerships.list"
                      />
                    }
                  />
                  <Route
                    path="/tools/spotlight-mismatches"
                    element={
                      <RoutePrivate
                        element={ScreensToolsSpotlightMismatches}
                        requiredPermission="spotlight.report"
                      />
                    }
                  />
                  <Route
                    path="/tools/viewership-data-audit"
                    element={
                      <RoutePrivate
                        element={ScreensToolsViewershipDataAudit}
                        requiredPermission="viewerships.list"
                      />
                    }
                  />
                  <Route
                    path="/tools/brandSpotlight"
                    element={
                      <RoutePrivate
                        element={ScreensToolsBrandSpotlight}
                        requiredPermission="brands.update"
                      />
                    }
                  />
                  <Route
                    path="/tools/batch-assign-placements"
                    element={
                      <RoutePrivate
                        element={ScreensToolsBatchAssignPlacements}
                        requiredPermission="placements.manage"
                      />
                    }
                  />
                  <Route
                    path="/tools/monthly-export"
                    element={
                      <RoutePrivate
                        element={ScreensToolsPlacementJsonExport}
                        requiredPermission="viewerships.adminControls"
                      />
                    }
                  />
                  <Route
                    path="/productions/*"
                    element={<ScreensProductions />}
                  />
                  <Route
                    path="/watch-productions"
                    element={
                      <RoutePrivate
                        element={ScreensWatchProductions}
                        requiredPermission="productions.manage"
                      />
                    }
                  />
                  <Route
                    path="/auditlog/:id"
                    element={
                      <RoutePrivate
                        element={ScreensAuditLogDetails}
                        requiredPermission="log.read"
                      />
                    }
                  />
                  <Route path="/user/*" element={<ScreensUser />} />
                  <Route path="/users/*" element={<ScreensUsers />} />
                  <Route
                    path="/traditional/campaigns/*"
                    element={<ScreensTraditionalCampaigns />}
                  />
                  <Route path="/sources/*" element={<ScreensSource />} />
                  <Route path="/scan/*" element={<ScreensScan />} />
                  <Route path="/placements/*" element={<ScreensPlacements />} />
                  <Route
                    path="/deliverables/*"
                    element={
                      <RoutePrivate
                        element={ScreensDeliverables}
                        requiredPermission="deliverables.manage"
                      />
                    }
                  />
                  <Route
                    path="/404"
                    element={<RoutePrivate element={ScreensNotFound404} />}
                  />
                  <Route
                    path="/not-pp-team"
                    element={<RoutePrivate element={ScreensNotPPTeamGate} />}
                  />
                  <Route
                    path="/"
                    element={
                      <RouteRedirect
                        replace
                        to={({
                          defaultLocation,
                        }: {
                          defaultLocation: string;
                        }) => defaultLocation}
                      />
                    }
                  />
                  <Route
                    path="*"
                    element={<RouteRedirect replace to="/404" />}
                  />
                </Routes>
              </BrowserRouter>
            </ApolloProvider>
          )}
        </ApplicationContext.Consumer>
      </ApplicationProvider>
    </DndProvider>
  );
}

export default Sentry.withProfiler(Root);
